import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios from 'axios';
import classNames from 'classnames';
import CdrDocumentAddEditModal from 'components/clinical-data/CdrDocumentAddEditModal';
import CdrDocumentHistoryModal from 'components/clinical-data/CdrDocumentHistoryModal';
import CdrDocumentPdfModal from 'components/clinical-data/CdrDocumentPdfModal';
import CdrDocumentTextModal from 'components/clinical-data/CdrDocumentTextModal';
import { includesImage } from 'components/clinical-data/documentUtils';
import PatientDocumentComments, { prepareComments } from 'components/clinical-data/PatientDocumentComments';
import ConfirmActionButton from 'components/ConfirmActionButton';
import LoaderComponent from 'components/LoaderComponent';
import ChangeEmployeeEmailModal from 'components/patient-entry/bookings-table/ChangeEmployeeEmailModal';
import PatientInfoView from 'components/patient-entry/bookings-table/PatientInfoView';
import {
  PatientCentralRefDataConsumerRenderProps,
  withPatientCentralRefDataContext
} from 'components/patient-entry/PatientCentralRefDataContext';
import _ from 'lodash';
import moment from 'moment';
import React, { useMemo, useRef, useState } from "react";
import { MessageSourceProps, withMessages } from 'react-message-source';
import { RouteComponentProps } from 'react-router';
import { DocumentState } from 'routes/UserNotificationsContext';
import { Button, Icon, Loader, Popup, SemanticCOLORS, SemanticICONS, Table } from 'semantic-ui-react';
import { getUpdatedPatientCdrDocumentDto } from 'services/patientCdrDocumentServices';
import {
  setAbnormalFindingFlag,
  setDiscussWithPatientFlag,
  setPatientDocumentArchiveFlag,
  setProcedureCreatedFlag,
  setRequiresActionFlag,
  setRequiresDoctorActionFlag
} from 'services/patientRecordServices';
import { getPatientForPatientCentral } from 'services/patientServices';
import { removeNotification } from 'services/userNotificationServices';
import styled from 'styled-components';
import { PatientDocumentType, PatientReportStatus } from 'ts-types/api.enums';
import {
  PatientCdrDocumentDto,
  PatientDocumentTypeDto,
  PatientDto,
  PatientInfoDto,
  UserDisplayDto,
  UserNotificationDto
} from 'ts-types/api.types';
import { isoToDisplayDate, isoToDisplayTimestamp } from 'utils/dateUtils';
import { errorUtils } from 'utils/errorUtils';
import { getText } from 'utils/textUtils';
import { withRouterWorkaround } from 'utils/workaroundUtils';

const TableInfoContainer = styled.div`
  flex-grow: 1;
  cursor: context-menu;
  overflow: auto;
  margin-top: 0.75rem;
  border: 1px solid rgba(34, 36, 38, .15);

  & .ui.table {
    border: none;
  }

  .ui.table.documents-table {

    td {
      vertical-align: baseline;
    }
  }

  & .ui.table thead tr:first-child > th {
    position: sticky !important;
    top: 0;
    z-index: 2;
    background: #fff;
  }

  & .ui.table td .description-cell {
    word-break: break-word;
  }

  .date-class {
    display: inline-flex;
    padding-right: 5px;
  }

  .icon-class {
    display: inline-flex;
  }

  .archived-class {
    color: gray;
  }
`;

const StyledTableRow = styled(Table.Row)`

  &.revoked {
    color: rgb(198, 198, 203)
  }

  &.active-notification td {
    background: rgb(163 51 200 / 5%);
    color: #707070;
  }

  & .resultStatusIcon {
    margin-left: .5rem
  }

  & .single-line-cell-class {
    max-width: 240px;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }

  & .patient-name {
    cursor: pointer;
  }

  & .patient-name:HOVER {
    color: #004ca6;
  }
`;

const StyledCell = styled(Table.Cell)`

  & .test-cert-container:not(:first-child) {
    margin-top: 0.75rem;
  }

  & .test-cert-container {
    display: flex;
    max-width: 6rem;
    justify-self: flex-start;

    .test-cert-member {
      flex: 50%;

      i.icon {
        cursor: pointer;
      }
    }

    .document-cert-member {
      flex: 50%;

      i.icon {
        cursor: pointer;
      }
    }
  }
`;

const PopupStyle = {
  borderRadius: 5,
  opacity: 0.7,
  padding: '7px'
};

const StyledButton = styled(Button)`
  padding: 2px !important;
  margin-bottom: 2px !important;
  background: transparent !important;
`;

interface DocumentActionProps extends MessageSourceProps {
  iconName: SemanticICONS;
  iconColor?: SemanticCOLORS;
  popupMessage: string;
  onClick: () => void;
}

const DocumentActionButtonComp: React.FC<DocumentActionProps> = (
    props: DocumentActionProps) => {

  return <StyledButton
      icon
      type="button"
      onClick={() => props.onClick()}
  >
    <Popup
        trigger={<Icon color={props.iconColor ? props.iconColor : "blue"} name={props.iconName}/>}
        content={props.getMessage(props.popupMessage)}
        size="small"
        position="top center"
        style={PopupStyle}
        inverted
    />
  </StyledButton>;
};

export const DocumentActionButton = withMessages(DocumentActionButtonComp);

interface Props extends MessageSourceProps,
    AuthConsumerRenderProps,
    RouteComponentProps,
    PatientCentralRefDataConsumerRenderProps {

  className?: string;
  documents: PatientCdrDocumentDto[];
  patientsMap: { [id: string]: PatientDto };
  doctorsMap: { [id: string]: PatientDto };
  documentTypesMap: { [id: string]: PatientDocumentTypeDto };
  currentUserDisplayData: UserDisplayDto;

  documentNotifications: DocumentState[] | undefined;
  showNotifications: boolean;

  setDocuments: (documents: PatientCdrDocumentDto[]) => void;
  refreshNotifications: () => void | undefined;

  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const RequiresActionDocumentList = (props: Props) => {

  const {
    getMessage,
    options
  } = props;

  const cancelTokenSource = useRef(axios.CancelToken.source());

  const [actionInProgress, setActionInProgress] =
      useState<boolean>(false);
  const [patientInfoLoading, setPatientInfoLoading] =
      useState<boolean>(false);
  const [patientInfo, setPatientInfo] =
      useState(undefined as PatientInfoDto | undefined);
  const [patientInfoPopupDocumentId, setPatientInfoPopupDocumentId] =
      useState(undefined as number | undefined);
  const [changeEmployeeMailPatientId, setChangeEmployeeMailPatientId] =
      useState(undefined as number | undefined);

  const docNotificationsMap = useMemo(
      () => _.keyBy(props.documentNotifications, "id"),
      [props.documentNotifications]);

  const getLastNotificationForDocument = (documentId: number) => {
    const docState = docNotificationsMap[documentId];
    if (docState) {
      return _.findLast(
          docState.notifications,
          note => note.type === "DOC_REQUIRES_DOCTOR_ACTION_SET");
    }
    return undefined;
  }

  const onSaveUpsertForm = async (
      successfullyUploaded: boolean,
      documentId: number | undefined) => {

    if (successfullyUploaded) {
      props.onSuccess(getMessage("patientRecord.patientDocument.successfullyUploadedMessage"));
    }
    if (documentId) {
      fetchUpdatedDocument(documentId);
    }
  }

  const handleError = (error: any) => {
    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [
      errorUtils.unableToRevokeForCurrentVaccination,
      errorUtils.invalidInput,
      errorUtils.invalidImage,
      errorUtils.imageFormatNotSupported,
      errorUtils.unknownPatientDocumentId,
      errorUtils.invalidDocumentId,
      "CLEAR_REQUIRES_DOCTOR_ACTION_NOT_ALLOWED"
    ];

    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          props.onError(
              getMessage(violation.details !== "" ? violation.details : `error.${violation.errorCode}`)
          )
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        props.onError(getMessage(`error.${errorCode}`))
      } else {
        props.onError(getMessage('error.general'));
      }
    }
  }

  const getDocumentTypeDescription = (documentType: string) => {
    const {
      documentTypesMap,
      language
    } = props;
    return getText(documentTypesMap[documentType].description, language);
  }

  const renderPatientDocumentName = (patientDocument: PatientCdrDocumentDto) => {
    return PatientDocumentType.UNABLE_TO_WORK !== patientDocument.type
    && PatientDocumentType.PRESCRIPTION !== patientDocument.type
        ? patientDocument.name
        : getDocumentTypeDescription(patientDocument.type);
  }

  const renderDoctorPatientDocumentCell = (doctorId: number): React.ReactNode => {
    let doctorName = "";
    const doctor = props.doctorsMap[doctorId];

    if (doctor) {
      const name = doctor.name !== null ? doctor.name : "";
      const firstName = doctor.firstName !== null ? doctor.firstName : "";

      doctorName = name + " " + firstName;
    }

    return doctorName;
  }

  const renderCommentCell = (document: PatientCdrDocumentDto) => {
    if (document == null || _.isEmpty(document.comments)) {
      return <></>;
    }

    return <PatientDocumentComments
        readonly
        comments={prepareComments(document.comments)}
    />;
  }

  const renderDownloadPatientDocumentCell = (patientDocument: PatientCdrDocumentDto) => {
    return <>
      <div className="test-cert-container">
        <div className="document-cert-member">
          <div>
            <ConfirmActionButton
                popupMessage={patientDocument.requiresAction
                    ? getMessage("patientRecord.patientDocument.removeRequiresAction")
                    : getMessage("patientRecord.patientDocument.setRequiresAction")
                }
                popupPosition="bottom center"
                trigger={
                  <Icon
                      title={getMessage("patientRecord.patientDocument.requiresAction")}
                      name={'warning circle'}
                      color={patientDocument.requiresAction ? 'yellow' : 'grey'}
                      style={{cursor: 'pointer'}}
                  />
                }
                onConfirm={() => changeRequiresActionFlag(patientDocument)}/>
          </div>
        </div>

        <div className="document-cert-member">
          <div>
            <ConfirmActionButton
                popupMessage={patientDocument.requiresDoctorAction
                    ? getMessage("patientRecord.patientDocument.removeRequiresDoctorAction")
                    : getMessage("patientRecord.patientDocument.setRequiresDoctorAction")
                }
                popupPosition="bottom center"
                trigger={
                  <Icon
                      title={getMessage("patientRecord.patientDocument.requiresDoctorAction")}
                      name={patientDocument.requiresDoctorAction ? "bell" : "bell slash outline"}
                      color={patientDocument.requiresDoctorAction ? 'purple' : 'grey'}
                      style={{cursor: 'pointer'}}
                  />
                }
                onConfirm={() => changeRequiresDoctorActionFlag(patientDocument)}/>
          </div>
        </div>

        <div className="document-cert-member">
          <div>
            <ConfirmActionButton
              popupMessage={patientDocument.abnormalFinding
                  ? getMessage("patientRecord.patientDocument.removeAbnormalFinding")
                  : getMessage("patientRecord.patientDocument.setAbnormalFinding")
              }
              popupPosition="bottom center"
              trigger={
                <Icon
                  title={getMessage("patientRecord.patientDocument.abnormalFinding")}
                  name={patientDocument.abnormalFinding ? "flag" : "flag outline"}
                  color={patientDocument.abnormalFinding ? "red" : "grey"}
                  style={{ cursor: "pointer" }}
                />
              }
              onConfirm={() => changeAbnormalFindingFlag(patientDocument)}
            />
          </div>
        </div>

        <div className="document-cert-member">
          <div>
            <ConfirmActionButton
                popupMessage={patientDocument.discussWithPatient
                    ? getMessage("patientRecord.patientDocument.removeDiscussWithPatient")
                    : getMessage("patientRecord.patientDocument.setDiscussWithPatient")
                }
                popupPosition="bottom center"
                trigger={
                  <Icon
                      title={getMessage("patientRecord.patientDocument.discussWithPatient")}
                      name={patientDocument.discussWithPatient ? "phone" : "phone"}
                      color={patientDocument.discussWithPatient ? "blue" : "grey"}
                      style={{cursor: 'pointer'}}
                  />
                }
                onConfirm={() => changeDiscussWithPatientActionFlag(patientDocument)}/>
          </div>
        </div>

        <div className="document-cert-member">
          <div>
            <ConfirmActionButton
              popupMessage={
                patientDocument.procedureCreated
                  ? getMessage("patientRecord.patientDocument.removeProcedureCreated")
                  : getMessage("patientRecord.patientDocument.setProcedureCreated")
              }
              popupPosition="bottom center"
              trigger={
                <Icon
                  title={getMessage("patientRecord.patientDocument.procedureCreated")}
                  name={patientDocument.procedureCreated ? "dollar" : "dollar"}
                  color={patientDocument.procedureCreated ? "green" : "grey"}
                  style={{ cursor: "pointer" }}
                />
              }
              onConfirm={() => changeProcedureCreatedFlag(patientDocument)}
            />
          </div>
        </div>
      </div>

      <div className="test-cert-container">
        {(patientDocument.documentFile || patientDocument.documentText) &&
            <div className="document-cert-member">
              {renderDocumentPreviewAction(patientDocument)}
            </div>
        }

        {
            includesImage(patientDocument)
            && patientDocument.documentText
            && <CdrDocumentTextModal document={patientDocument}
                                     handleError={handleError}
            />
        }

        {
            patientDocument.manualUpload
            && <CdrDocumentAddEditModal document={patientDocument}
                                        patient={props.patientsMap[patientDocument.patientId]}
                                        onSave={onSaveUpsertForm}
                                        handleError={handleError}
            />
        }

        {/* this needs to consider the cases where the treatment
              can't be modified (deleted/closed/billed)
          {!patientDocument.manualUpload && (
                  patientDocument.type === PatientDocumentType.PRESCRIPTION ||
                  patientDocument.type === PatientDocumentType.UNABLE_TO_WORK) &&
              <EditActionButton
                  hoverMessage={"Edit report"}
                  onConfirm={() => this.openEditReportModal(patientDocument)}/>
          }*/}

        <div className="document-cert-member">
          <ConfirmActionButton
              popupMessage={patientDocument.archived
                  ? getMessage("patientRecord.patientDocument.unarchiveDocument")
                  : getMessage("patientRecord.patientDocument.archiveDocument")}
              popupPosition="bottom center"
              trigger={<Icon
                  name={'archive'}
                  color={patientDocument.archived ? 'grey' : 'blue'}
                  style={{cursor: 'pointer'}}/>}
              onConfirm={() => changePatientDocumentArchiveFlag(patientDocument)}/>
        </div>

        <div className="document-cert-member">
          {renderDocumentHistory(patientDocument)}
        </div>
      </div>
    </>
  };

  const renderDocumentPreviewAction = (document: PatientCdrDocumentDto) => {

    // INFO vg 2025-02-12: remove special handling for prescriptions,
    // to enable adding comments for prescription documents
    if (document.documentFile) {
      return <CdrDocumentPdfModal
          document={document}
          currentUserDisplayData={props.currentUserDisplayData}
          iconTooltip={getDocumentTypeDescription(document.type)}
          handleError={handleError}
          onClose={(docId: number) => fetchUpdatedDocument(docId)}
      />;
    } else if (document.documentText) {
      return <CdrDocumentTextModal
          document={document}
          handleError={handleError}
      />;
    }

  }

  const fetchUpdatedDocument = async (documentId: number | undefined) => {

    if (documentId == undefined) {
      return;
    }

    const updatedPatientMedicalDocument =
        await getUpdatedPatientCdrDocumentDto(documentId, cancelTokenSource.current);

    const newPatientDocuments = props.documents.map(patientDocument => {
      if (patientDocument.documentId == documentId) {
        patientDocument = {...updatedPatientMedicalDocument}
        return patientDocument;
      } else {
        return patientDocument;
      }
    });

    props.setDocuments(newPatientDocuments);
  }

  const changePatientDocumentArchiveFlag = async (patientMedicalDocument: PatientCdrDocumentDto) => {
    const type = patientMedicalDocument.type;

    let documentId = patientMedicalDocument.documentId;
    let patientId = patientMedicalDocument.patientId;

    const manualUpload = patientMedicalDocument.manualUpload;
    if (PatientDocumentType.UNABLE_TO_WORK === type && !manualUpload) {
      documentId = patientMedicalDocument.unfitId !== null
          ? patientMedicalDocument.unfitId
          : patientMedicalDocument.documentId;
    } else if (PatientDocumentType.PRESCRIPTION === type && !manualUpload) {
      documentId = patientMedicalDocument.prescriptionId !== null
          ? patientMedicalDocument.prescriptionId
          : patientMedicalDocument.documentId;
    }

    await setPatientDocumentArchiveFlag(
        patientId, documentId, type, !patientMedicalDocument.archived, cancelTokenSource.current);

    fetchUpdatedDocument(documentId);
  };

  const changeRequiresActionFlag = (patientMedicalDocument: PatientCdrDocumentDto) => {
    let documentId = patientMedicalDocument.documentId;
    let patientId = patientMedicalDocument.patientId;

    const requiresActionNewValue = !patientMedicalDocument.requiresAction;
    setRequiresActionFlag(
        patientId, documentId, requiresActionNewValue, cancelTokenSource.current)
        .then(response => {
          props.onSuccess(
              requiresActionNewValue
                  ? getMessage("patientRecord.patientDocument.successfullySetRequiresAction")
                  : getMessage("patientRecord.patientDocument.successfullyRemovedRequiresAction")
          );

          fetchUpdatedDocument(documentId);
        })
        .catch((e: any) => handleError(e.response.data));
  };

  const changeAbnormalFindingFlag = (patientMedicalDocument: PatientCdrDocumentDto) => {
    let documentId = patientMedicalDocument.documentId;
    let patientId = patientMedicalDocument.patientId;

    const abnormalFindingFlagNewValue = !patientMedicalDocument.abnormalFinding;
    setAbnormalFindingFlag(
        patientId, documentId, abnormalFindingFlagNewValue, cancelTokenSource.current)
        .then(response => {
          props.onSuccess(
              abnormalFindingFlagNewValue
                  ? getMessage("patientRecord.patientDocument.successfullySetAbnormalFinding")
                  : getMessage("patientRecord.patientDocument.successfullyRemovedAbnormalFinding")
          );

          fetchUpdatedDocument(documentId);
        })
        .catch((e: any) => handleError(e.response.data));
  };

  const changeRequiresDoctorActionFlag = async (patientMedicalDocument: PatientCdrDocumentDto) => {
    let documentId = patientMedicalDocument.documentId;
    let patientId = patientMedicalDocument.patientId;

    const requiresDoctorActionNewValue = !patientMedicalDocument.requiresDoctorAction;
    try {
      await setRequiresDoctorActionFlag(
          patientId, documentId, requiresDoctorActionNewValue, cancelTokenSource.current);

      props.onSuccess(
          requiresDoctorActionNewValue
              ? getMessage("patientRecord.patientDocument.successfullySetRequiresDoctorAction")
              : getMessage("patientRecord.patientDocument.successfullyRemovedRequiresDoctorAction")
      );

      fetchUpdatedDocument(documentId);

      if (props.showNotifications) {
        const docState = docNotificationsMap[documentId];
        const docNotes = docState.notifications;
        await removeNotification(docNotes[docNotes.length - 1].id, cancelTokenSource.current);

        setTimeout(() => props.refreshNotifications && props.refreshNotifications(), 750);
      }

    } catch (error) {
      error && error.response && handleError(error.response.data)
    }
  };

  const changeDiscussWithPatientActionFlag = async (patientMedicalDocument: PatientCdrDocumentDto) => {
    let documentId = patientMedicalDocument.documentId;
    let patientId = patientMedicalDocument.patientId;

    const discussWithPatientNewValue = !patientMedicalDocument.discussWithPatient;
    try {
      await setDiscussWithPatientFlag(
          patientId, documentId, discussWithPatientNewValue, cancelTokenSource.current);

      props.onSuccess(
          discussWithPatientNewValue
              ? getMessage("patientRecord.patientDocument.successfullySetDiscussWithPatient")
              : getMessage("patientRecord.patientDocument.successfullyRemovedDiscussWithPatient")
      );

      fetchUpdatedDocument(documentId);

    } catch (error) {
      error && error.response && handleError(error.response.data)
    }
  };

  const changeProcedureCreatedFlag = async (patientMedicalDocument: PatientCdrDocumentDto) => {
    let documentId = patientMedicalDocument.documentId;
    let patientId = patientMedicalDocument.patientId;

    const procedureCreatedNewValue = !patientMedicalDocument.procedureCreated;
    try {
      await setProcedureCreatedFlag(
          patientId, documentId, procedureCreatedNewValue, cancelTokenSource.current);

      props.onSuccess(
          procedureCreatedNewValue
              ? getMessage("patientRecord.patientDocument.successfullySetProcedureCreated")
              : getMessage("patientRecord.patientDocument.successfullyRemovedProcedureCreated")
      );

      fetchUpdatedDocument(documentId);

    } catch (error) {
      error && error.response && handleError(error.response.data)
    }
  };

  const renderDocumentHistory = (patientDocument: PatientCdrDocumentDto) => {
    return (
        patientDocument.documentId !== null &&
        <CdrDocumentHistoryModal
            document={patientDocument}
            doctorsMap={props.doctorsMap}
            documentTypesMap={props.documentTypesMap}
            handleError={handleError}
        />
    );
  }

  const openEditEmailModal = (patientId: number) => {
    setPatientInfoPopupDocumentId(undefined);
    setPatientInfo(undefined);
    setChangeEmployeeMailPatientId(patientId)
  }

  const onPatientNameClick = (patientId: number, documentId: number) => {
    setPatientInfoLoading(true);

    getPatientForPatientCentral(patientId, cancelTokenSource.current)
        .then(response => {
          setPatientInfo(response);
          setPatientInfoPopupDocumentId(documentId);
        })
        .finally(() => {
          setPatientInfoLoading(false)
        });
  };

  const clearPatientInfoState = () => {
    setPatientInfo(undefined);
    setPatientInfoPopupDocumentId(undefined);
  }

  const patientNameCellRenderer = (documentId: number, patient: any, sexDisplay: string) => {

    const loader = (
        <Loader active inline="centered" size="small">{getMessage("loading")}</Loader>
    );

    const patientPopup = (popupTrigger: any) => (
        <Popup
            trigger={popupTrigger}
            open={documentId === patientInfoPopupDocumentId}
            popperDependencies={[patientInfoLoading]}
            popperModifiers={{
              preventOverflow: {
                boundary: document.getElementById("patientCentralMainContent")
              }
            }}
            content={
              patientInfoLoading
                  ? loader
                  : <PatientInfoView
                      showButtons={true}
                      patientInfo={patientInfo}
                      onEditEmailButtonClick={openEditEmailModal}
                  />
            }
            flowing
            on="click"
            onClose={clearPatientInfoState}
            size="large"
            position="right center"
        />
    );

    let trigger = <>
      <div className="patient-name">
        {patient.name} <b>{patient.firstName}</b>,&nbsp;
        {isoToDisplayDate(patient.birthDate)},&nbsp;
        {sexDisplay}
      </div>
      <div>[{patient.id}]</div>
    </>

    if (documentId) {
      return (
          <div title={getMessage("patient")}
               onClick={() => onPatientNameClick(patient.id, documentId)}>
            {patientPopup(trigger)}
          </div>
      );
    }

    return trigger;
  }

  const renderNotificationCell = (lastNotificationForDocument: UserNotificationDto | undefined) => {

    // const lastNotificationForDocument =
    //   getLastNotificationForDocument(patientDocument.id);
    if (!lastNotificationForDocument) {
      return <div>n/a</div>;
    }

    return <div>
      <div>{isoToDisplayTimestamp(lastNotificationForDocument!.timestamp)}</div>
      <div>{lastNotificationForDocument!.createdByUserFullName}</div>
    </div>
  }

  return <TableInfoContainer className={props.className || ""}>
    {
        (actionInProgress)
        && <LoaderComponent message={getMessage("loading")}/>
    }
    <Table className="documents-table" compact="very">
      <Table.Header>
        <Table.Row>
          {
              props.showNotifications
              && <Table.HeaderCell><Icon name={"bell"}/></Table.HeaderCell>
          }
          <Table.HeaderCell>{getMessage("patientRecord.patientDocument.documentDate")}</Table.HeaderCell>
          <Table.HeaderCell>{getMessage("patient")}</Table.HeaderCell>
          <Table.HeaderCell>
            {getMessage("patientRecord.patientDocument.type")}&nbsp;/&nbsp;
            {getMessage("patientRecord.patientDocument.name")}&nbsp;/&nbsp;
            {getMessage("patientRecord.patientDocument.patientDescription")}&nbsp;/&nbsp;
            {getMessage("patientRecord.patientDocument.uploadPatientDocument.doctor")}
          </Table.HeaderCell>
          <Table.HeaderCell>{getMessage("patientRecord.patientDocument.uploadPatientDocument.comment")}</Table.HeaderCell>
          <Table.HeaderCell>{getMessage("patientRecord.patientDocument.actionButtons")}</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {
          props.documents.map(patientDocument => {

            const patient = props.patientsMap[patientDocument.patientId];
            const sex = patient.sex;
            let sexDisplay = "";
            if (sex === 0) {
              sexDisplay = "male";
            } else if (sex === 1) {
              sexDisplay = "female";
            } else if (sex === 2) {
              sexDisplay = "other";
            }
            sexDisplay = getMessage(`gender.${sexDisplay}`).substr(0, 1).toLowerCase();

            const lastNotificationForDocument =
                getLastNotificationForDocument(patientDocument.id);

            return <StyledTableRow key={patientDocument.id}
                                   className={classNames([
                                     {
                                       "no-active-notification": props.showNotifications && !lastNotificationForDocument,
                                       "active-notification": props.showNotifications && lastNotificationForDocument
                                     }
                                   ])}
            >
              {
                  props.showNotifications
                  && <Table.Cell className={`notification-info`}
                  >
                    {renderNotificationCell(lastNotificationForDocument)}
                  </Table.Cell>
              }
              <Table.Cell
                  className={`document-date ${patientDocument.requiresAction ? "requires-action" : ""}`}
              >
                <div>
                  {moment(patientDocument.documentDate).format("DD.MM.YYYY")}
                </div>
                {
                    patientDocument.requiresAction &&
                    <Icon name="warning circle"
                          color="yellow"
                          title={getMessage("patientRecord.patientDocument.requiresAction")}
                    />
                }
                {
                    patientDocument.requiresDoctorAction &&
                    <Icon name={patientDocument.requiresDoctorAction ? "bell" : "bell slash outline"}
                          color="purple"
                          title={getMessage("patientRecord.patientDocument.requiresDoctorAction")}
                    />
                }
                {
                    PatientReportStatus.CONTROLLED === patientDocument.reportStatus &&
                    <Icon name="check circle"
                          color="green"
                          title={getMessage("patientRecord.patientDocument.controlledReport")}
                    />
                }
              </Table.Cell>
              <Table.Cell className={patientDocument.archived ? "archived-class" : ""}>
                {patientNameCellRenderer(patientDocument.documentId, patient, sexDisplay)}
                {
                    patient.externalIdManual &&
                    <div>[{patient.externalIdManual}]</div>
                }
                {
                    patient.externalId &&
                    <div>[<Icon name="mobile alternate" size="small"/> {patient.externalId}]</div>
                }
              </Table.Cell>

              <Table.Cell className={patientDocument.archived ? "document-cell archived-class" : "document-cell"}>
                <div className="document-type">
                  {getDocumentTypeDescription(patientDocument.type)}
                </div>
                <div className="document-name">
                  {renderPatientDocumentName(patientDocument)}
                </div>
                <div className="document-description">
                  <span className="description-cell">{patientDocument.description}</span>
                </div>
                {
                    patientDocument.doctorId &&
                    <div className="document-doctor">
                      <Icon name="doctor"/>
                      {renderDoctorPatientDocumentCell(patientDocument.doctorId)}
                    </div>
                }
              </Table.Cell>

              <Table.Cell
                  className={patientDocument.archived ? "single-line-cell-class archived-class" : "single-line-cell-class"}>
                {renderCommentCell(patientDocument)}
              </Table.Cell>

              <StyledCell>
                {renderDownloadPatientDocumentCell(patientDocument)}
              </StyledCell>
            </StyledTableRow>;
          })
        }
      </Table.Body>
    </Table>

    {
        changeEmployeeMailPatientId &&
        <ChangeEmployeeEmailModal
            open={true}
            patientId={changeEmployeeMailPatientId}
            onClose={() => {
              setChangeEmployeeMailPatientId(undefined)
            }}
        />
    }
  </TableInfoContainer>;
}

export default withPatientCentralRefDataContext(
    withAuthContext(
        withRouterWorkaround(
            withMessages(RequiresActionDocumentList))));
